import type IProduct from "@api/models/IProduct";
import ButtonVariants from "@core-components/button/ButtonVariants";
import SSHeadline from "@core-components/title/SSHeadline";
import SSWhiteText from "@core-components/title/components/SSWhiteText";
import useAppTranslation from "@hooks/language/useAppTranslation";
import { SimpleGrid } from "@mantine/core";
import type { TFunction } from "i18next";

const productShortName = (productSlug: string, t: TFunction) => {
  switch (productSlug) {
    case "handlevel-account-2600mmr":
    case "compte-monte-main-2600mmr":
      return t("accountTypes.mmr2600");

    case "handlevel-account-2500mmr":
    case "compte-monte-main-2500mmr":
      return t("accountTypes.mmr2500");

    case "handlevel-account-2400mmr":
    case "compte-monte-main-2400mmr":
      return t("accountTypes.mmr2400");

    case "handlevel-account-classique":
    case "compte-monte-main-classique":
      return t("accountTypes.classicMain");

    case "low-mmr-account":
    case "compte-mmr-bas":
      return t("accountTypes.lowMmr");

    case "ghost-account":
    case "compte-fantome":
      return t("accountTypes.ghost");

    case "prime-gaming-account":
    case "compte-prime-gaming":
      return t("accountTypes.primeGaming");

    case "ranked-ready-account":
    case "compte-ranked-ready":
      return t("accountTypes.rankedReady");

    case "ranked-ready-100-winrate":
      return t("accountTypes.rankedReady100Winrate");

    case "classic-account":
    case "compte-classique":
      return t("accountTypes.classic");

    default:
      console.log("default", productSlug);
      return productSlug;
  }
};

interface SkinProductTypeSelectorProps {
  skinProducts: IProduct[];
  selectedProduct: IProduct | undefined;
  setSelectedProduct: (product: IProduct) => void;
  unavailableIds: number[];
}

export const SkinProductTypeSelector = ({
  skinProducts,
  selectedProduct,
  setSelectedProduct,
  unavailableIds,
}: SkinProductTypeSelectorProps) => {
  const { t } = useAppTranslation("pages.skins.skinProducts");

  return (
    <>
      <SSHeadline
        props={{
          title: {
            fz: 15,
          },
        }}
      >
        <SSWhiteText>{t("selectAccountType")}</SSWhiteText>
      </SSHeadline>

      <SimpleGrid
        cols={{ base: 2, sm: 3 }}
        spacing={{ base: "xs", md: "xl" }}
        verticalSpacing={"md"}
      >
        {skinProducts?.map((product) => {
          const isBuyable =
            product.accounts.filter((id) => !unavailableIds.includes(id))
              .length > 0;
          const label = productShortName(product.slug, t).toUpperCase();

          return (
            <ButtonVariants.Gradient
              key={product.slug}
              className={`rounded-full ${
                isBuyable
                  ? "!border-solid border-2 border-secret-blue-light"
                  : ""
              }`}
              disabled={!isBuyable}
              isGradient={product.slug === selectedProduct?.slug}
              label={label}
              tooltip={label}
              onClick={() => setSelectedProduct(product)}
            />
          );
        })}
      </SimpleGrid>
    </>
  );
};
